import { useEffect, useState } from "react";
import { HandCoins, Check, Banknote, CreditCard, Building2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { TRY, todayISO } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DatePicker } from "@/components/DatePicker";
import { receiptBelgeNo } from "@/components/QuickSaleHistoryList";
import { veresiyeRemaining } from "@/lib/pos";

const METHODS = [["nakit", "Nakit", Banknote, "border-emerald-500 bg-emerald-50 text-emerald-700"], ["kredi_karti", "Kredi Kartı", CreditCard, "border-blue-500 bg-blue-50 text-blue-700"], ["havale", "Havale / EFT", Building2, "border-violet-500 bg-violet-50 text-violet-700"]];
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
export { veresiyeRemaining };

// Veresiye fişin tahsilatı — tek tık: kalan tutar + yöntem seçilir, cari deftere fişe bağlı tahsilat yazılır.
export function VeresiyeCollectDialog({ sale, onClose, onDone }) {
  const [method, setMethod] = useState("nakit");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(todayISO());
  const [saving, setSaving] = useState(false);
  const remaining = veresiyeRemaining(sale);
  useEffect(() => { if (sale) { setMethod("nakit"); setAmount(String(veresiyeRemaining(sale))); setDate(todayISO()); } }, [sale]);
  const save = async () => {
    const amt = r2(String(amount).replace(",", "."));
    if (!(amt > 0)) return toast.error("Tutar girin");
    if (amt > remaining + 0.005) return toast.error(`Tutar kalan borcu (${TRY(remaining)}) aşamaz`);
    setSaving(true);
    try {
      const { data } = await api.post(`/quick-sales/${sale.id}/collect`, { method, amount: amt, date });
      toast.success(data.veresiye_paid ? `${receiptBelgeNo(sale)} tahsil edildi · ${TRY(amt)}` : `${TRY(amt)} tahsil edildi · kalan ${TRY(data.remaining)}`);
      onDone && onDone(data);
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!sale} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-[96vw] sm:max-w-md" data-testid="veresiye-collect-dialog">
        <DialogHeader><DialogTitle className="font-head text-xl flex items-center gap-2"><HandCoins className="h-5 w-5 text-amber-600" />Veresiye Tahsilatı</DialogTitle></DialogHeader>
        {sale && (
          <div className="space-y-4">
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm">
              <div className="flex items-center justify-between gap-2"><span className="font-mono font-bold text-slate-700">{receiptBelgeNo(sale)}</span><span className="text-xs text-muted-foreground">{sale.cari_contact_title || sale.title}</span></div>
              <div className="mt-1 flex items-baseline justify-between"><span className="text-xs text-amber-800">Kalan borç</span><span className="font-head font-black text-2xl tnum text-amber-800" data-testid="veresiye-collect-remaining">{TRY(remaining)}</span></div>
              {Number(sale.collected_total) > 0 && <div className="text-[11px] text-muted-foreground">Önceki tahsilat: {TRY(sale.collected_total)} · Fiş toplamı {TRY(sale.grand_total)}</div>}
              {Number(sale.veresiye_amount) > 0 && sale.payment_method === "karma" && <div className="text-[11px] text-muted-foreground">Parçalı ödeme · veresiye kısmı {TRY(sale.veresiye_amount)} · Fiş toplamı {TRY(sale.grand_total)}</div>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Tutar</Label><Input type="number" inputMode="decimal" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 h-10 tnum font-bold" data-testid="veresiye-collect-amount" /></div>
              <div><Label className="text-xs">Tarih</Label><div className="mt-1"><DatePicker value={date} onChange={(v) => v && setDate(v)} className="h-10 w-full justify-start" data-testid="veresiye-collect-date" /></div></div>
            </div>
            <div>
              <Label className="text-xs">Ödeme Yöntemi</Label>
              <div className="mt-1 grid grid-cols-3 gap-2">
                {METHODS.map(([val, lbl, Icon, on]) => (
                  <button key={val} type="button" onClick={() => setMethod(val)} data-testid={`veresiye-collect-${val}`}
                    className={cn("flex flex-col items-center gap-1 rounded-xl border-2 p-2.5 text-xs font-semibold transition-all", method === val ? on : "border-border hover:border-slate-300")}>
                    <Icon className="h-5 w-5" />{lbl}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={onClose} data-testid="veresiye-collect-cancel">Vazgeç</Button>
              <Button onClick={save} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="veresiye-collect-confirm"><Check className="h-4 w-4 mr-1.5" />{saving ? "Kaydediliyor…" : "Tahsil Et"}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
